(() => {
  const FOOTIBOX_HANDLE = 'la-footibox';
  const POSTER_VARIANT_ID = 54305661485389;
  const GIFT_PROPERTY = '_Cadeau Footibox';
  const GIFT_VALUE = 'Poster Le Numéro 7';

  let syncPromise = null;

  /**
   * Lit le panier Shopify actuel.
   */
  async function getCart() {
    const response = await fetch(
      window.Shopify.routes.root + 'cart.js',
      {
        headers: {
          Accept: 'application/json'
        },
        cache: 'no-store'
      }
    );

    if (!response.ok) {
      throw new Error(
        'Impossible de lire le panier.'
      );
    }

    return response.json();
  }

  /**
   * Ajoute le poster cadeau.
   */
  async function addGiftPoster(quantity) {
    const response = await fetch(
      window.Shopify.routes.root + 'cart/add.js',
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        },
        body: JSON.stringify({
          items: [
            {
              id: POSTER_VARIANT_ID,
              quantity: quantity,
              properties: {
                [GIFT_PROPERTY]: GIFT_VALUE
              }
            }
          ]
        })
      }
    );

    if (!response.ok) {
      const error = await response
        .json()
        .catch(() => ({}));

      throw new Error(
        error.description ||
        error.message ||
        "Impossible d'ajouter le poster cadeau."
      );
    }
  }

  /**
   * Change la quantité d'une ligne existante.
   * quantity = 0 permet de supprimer la ligne.
   */
  async function changeLine(
    lineKey,
    quantity
  ) {
    const response = await fetch(
      window.Shopify.routes.root + 'cart/change.js',
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        },
        body: JSON.stringify({
          id: lineKey,
          quantity: quantity
        })
      }
    );

    if (!response.ok) {
      throw new Error(
        'Impossible de corriger le poster cadeau.'
      );
    }
  }

  /**
   * Indique au thème que le panier a changé.
   * Cela permet notamment au cart drawer
   * de rafraîchir son contenu.
   */
  function announceCartUpdate() {
    try {
      if (
        typeof publish === 'function' &&
        typeof PUB_SUB_EVENTS !== 'undefined' &&
        PUB_SUB_EVENTS.cartUpdate
      ) {
        publish(
          PUB_SUB_EVENTS.cartUpdate,
          {
            source:
              'footibox-gift-safety'
          }
        );
      }
    } catch (error) {
      console.error(
        'Erreur rafraîchissement panier Footibox :',
        error
      );
    }
  }

  /**
   * Vérifie et répare le panier.
   *
   * Exemples :
   *
   * 1 Footibox + 0 poster
   * => ajoute 1 poster
   *
   * 2 Footibox + 1 poster
   * => passe le poster à 2
   *
   * 0 Footibox + poster cadeau
   * => supprime le poster cadeau
   *
   * Plusieurs lignes de poster cadeau
   * => garde une seule ligne propre
   */
  async function performGiftSync() {
    const cart = await getCart();

    const footiboxQuantity =
      cart.items
        .filter(
          (item) =>
            item.handle ===
            FOOTIBOX_HANDLE
        )
        .reduce(
          (total, item) =>
            total + item.quantity,
          0
        );

    const giftPosterLines =
      cart.items.filter(
        (item) =>
          Number(item.variant_id) ===
            POSTER_VARIANT_ID &&
          item.properties &&
          item.properties[
            GIFT_PROPERTY
          ]
      );

    const giftPosterQuantity =
      giftPosterLines.reduce(
        (total, item) =>
          total + item.quantity,
        0
      );

    /*
     * Tout est déjà correct.
     */
    if (
      footiboxQuantity ===
        giftPosterQuantity &&
      giftPosterLines.length <= 1
    ) {
      return false;
    }

    /*
     * Aucune Footibox :
     * on supprime tous les posters
     * marqués comme cadeaux Footibox.
     */
    if (footiboxQuantity === 0) {
      for (
        const giftLine of giftPosterLines
      ) {
        await changeLine(
          giftLine.key,
          0
        );
      }

      return (
        giftPosterLines.length > 0
      );
    }

    /*
     * Footibox présente mais
     * aucun poster cadeau.
     */
    if (
      footiboxQuantity > 0 &&
      giftPosterLines.length === 0
    ) {
      await addGiftPoster(
        footiboxQuantity
      );

      return true;
    }

    /*
     * Un ou plusieurs posters existent.
     *
     * On utilise la première ligne
     * comme ligne cadeau principale.
     */
    const mainGiftLine =
      giftPosterLines[0];

    if (
      mainGiftLine.quantity !==
      footiboxQuantity
    ) {
      await changeLine(
        mainGiftLine.key,
        footiboxQuantity
      );
    }

    /*
     * Si plusieurs lignes cadeau
     * existent par erreur,
     * on supprime les doublons.
     */
    if (
      giftPosterLines.length > 1
    ) {
      for (
        let i = 1;
        i <
        giftPosterLines.length;
        i++
      ) {
        await changeLine(
          giftPosterLines[i].key,
          0
        );
      }
    }

    return true;
  }

  /**
   * Empêche deux réparations
   * simultanées.
   */
  async function syncGift() {
    if (syncPromise) {
      return syncPromise;
    }

    syncPromise =
      performGiftSync();

    try {
      const changed =
        await syncPromise;

      if (changed) {
        announceCartUpdate();
      }

      return changed;
    } catch (error) {
      console.error(
        'Erreur sécurité cadeau Footibox :',
        error
      );

      return false;
    } finally {
      syncPromise = null;
    }
  }

  /**
   * Vérification immédiate.
   *
   * Important pour les anciens paniers
   * restaurés lorsque le client revient
   * sur le site.
   */
  syncGift();

  /**
   * Nouvelle vérification dès que
   * le document est complètement prêt.
   */
  if (
    document.readyState ===
    'loading'
  ) {
    document.addEventListener(
      'DOMContentLoaded',
      syncGift,
      { once: true }
    );
  }

  /**
   * Important sur mobile :
   * "pageshow" se déclenche aussi
   * lors du retour vers une page
   * restaurée depuis le cache navigateur.
   */
  window.addEventListener(
    'pageshow',
    () => {
      syncGift();
    }
  );

  /**
   * Quand le client revient sur
   * l'onglet/la fenêtre.
   */
  window.addEventListener(
    'focus',
    () => {
      syncGift();
    }
  );

  /**
   * Quand la page redevient visible.
   */
  document.addEventListener(
    'visibilitychange',
    () => {
      if (
        document.visibilityState ===
        'visible'
      ) {
        syncGift();
      }
    }
  );

  /**
   * Si le thème annonce qu'un panier
   * vient d'être modifié, on revérifie
   * également quelques instants après.
   *
   * Cela ne bloque rien.
   */
  if (
    typeof subscribe === 'function' &&
    typeof PUB_SUB_EVENTS !==
      'undefined' &&
    PUB_SUB_EVENTS.cartUpdate
  ) {
    subscribe(
      PUB_SUB_EVENTS.cartUpdate,
      (event) => {
        if (
          event &&
          event.source ===
            'footibox-gift-safety'
        ) {
          return;
        }

        window.setTimeout(
          syncGift,
          100
        );
      }
    );
  }

  /**
   * Sécurité supplémentaire au chargement.
   *
   * Certains navigateurs intégrés
   * restaurent le panier ou certains
   * scripts avec un léger retard.
   *
   * On revérifie donc rapidement
   * quelques fois après l'ouverture,
   * puis on s'arrête.
   */
  const startupChecks = [
    500,
    1500,
    3000
  ];

  startupChecks.forEach(
    (delay) => {
      window.setTimeout(
        syncGift,
        delay
      );
    }
  );
})();